import clsx from "clsx";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type ActionColor = "blue" | "amber" | "red" | "green" | "slate";

type ActionButtonBaseProps = {
  children: ReactNode;
  color?: ActionColor;
  className?: string;
};

type ActionButtonProps = ActionButtonBaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    as?: "button";
  };

type ActionChipProps = ActionButtonBaseProps & {
  as: "chip";
};

const COLOR_CLASSES: Record<ActionColor, string> = {
  blue: "bg-accent-blue/15 text-accent-blue hover:bg-accent-blue/25",
  amber: "bg-accent-amber/10 text-accent-amber hover:bg-accent-amber/20",
  red: "bg-accent-red/10 text-accent-red hover:bg-accent-red/20",
  green: "bg-accent-green/10 text-accent-green hover:bg-accent-green/20",
  slate: "bg-slate-100 text-ink-secondary hover:bg-slate-200",
};

const BASE_CLASSES =
  "rounded-md px-2 py-1.5 text-xs font-medium transition-colors";

export const actionLinkClasses = (color: ActionColor = "blue") =>
  clsx(BASE_CLASSES, COLOR_CLASSES[color]);

export const ActionButton = (props: ActionButtonProps | ActionChipProps) => {
  const colorClasses = COLOR_CLASSES[props.color ?? "blue"];
  if (props.as === "chip") {
    return (
      <span className={clsx(BASE_CLASSES, colorClasses, props.className)}>
        {props.children}
      </span>
    );
  }
  const { as: _as, color: _color, className, children, ...rest } =
    props as ActionButtonProps;
  return (
    <button
      type={rest.type ?? "button"}
      {...rest}
      className={clsx(
        BASE_CLASSES,
        colorClasses,
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
};